<template>
  <div class="auth-form">
    <div class="auth-form__intro">
      <p class="auth-eyebrow">Welcome back</p>
      <h1>Sign in to your classroom</h1>
      <p>Enter the school details provided by your administrator.</p>
    </div>

    <form novalidate @submit.prevent="submit">
      <div class="auth-field-group">
        <label class="auth-field-label" for="teacher-school-code">School code</label>
        <v-text-field
          id="teacher-school-code"
          v-model.trim="code"
          outlined
          ref="locationCode"
          :error-messages="codeErrors"
          placeholder="Enter your school code"
          autocomplete="off"
          autocapitalize="none"
          spellcheck="false"
          prepend-inner-icon="mdi-domain"
          @keyup.enter="focusEmail"
        />
      </div>

      <div class="auth-field-group">
        <label class="auth-field-label" for="teacher-email">Email address</label>
        <v-text-field
          id="teacher-email"
          v-model.trim="email"
          ref="email"
          outlined
          :error-messages="emailErrors"
          placeholder="you@example.com"
          autocomplete="email"
          inputmode="email"
          prepend-inner-icon="mdi-email-outline"
          @keyup.enter="focusPassword"
        />
      </div>

      <div class="auth-field-group auth-field-group--last">
        <label class="auth-field-label" for="teacher-password">Password</label>
        <v-text-field
          id="teacher-password"
          v-model="password"
          ref="password"
          outlined
          :error-messages="passwordErrors"
          placeholder="Enter your password"
          :type="showPassword ? 'text' : 'password'"
          :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
          prepend-inner-icon="mdi-lock-outline"
          autocomplete="current-password"
          @click:append="showPassword = !showPassword"
          @input="$v.password.$touch()"
          @keyup.enter="submit"
        />
      </div>

      <div class="auth-form__help">
        <button type="button" class="auth-text-button" @click="$router.push({ path: '/forgot' })">
          Forgot password?
        </button>
      </div>

      <v-btn class="auth-primary-button" block x-large :loading="loading" color="primary" type="submit">
        {{ $t("buttons.login") }}
        <v-icon right size="20">mdi-arrow-right</v-icon>
      </v-btn>
    </form>

    <div class="auth-form__secondary">
      <span>New to Taleem?</span>
      <button type="button" class="auth-text-button" @click="$router.push({ path: '/signup' })">
        Create an account
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import authService from "../service";

const { validationMixin } = require("vuelidate");
const { required, email } = require("vuelidate/lib/validators");

export default {
  name: "authentication-login",
  mixins: [validationMixin],
  data() {
    return {
      showPassword: false,
      email: null,
      loading: false,
      password: null,
      code: null,
    };
  },
  created() {
    const localStoreCode = localStorage.getItem(
      `${process.env.VUE_APP_STORAGE_PREFIX}code`
    );
    if (localStoreCode) this.code = localStoreCode;
  },
  mounted() {
    this.$nextTick(() => {
      // mobile-friendly focus
      if (!this.code && this.$refs.locationCode) this.$refs.locationCode.focus();
    });
  },
  validations: {
    email: { email, required },
    code: { required },
    password: { required },
  },
  computed: {
    codeErrors() {
      const errors = [];
      if (!this.$v.code.$dirty) return errors;
      if (!this.$v.code.required) errors.push("Code is required.");
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.email.$dirty) return errors;
      if (!this.$v.email.email) errors.push("Email is not valid");
      if (!this.$v.email.required) errors.push("Email is required.");
      return errors;
    },
    passwordErrors() {
      const errors = [];
      if (!this.$v.password.$dirty) return errors;
      if (!this.$v.password.required) errors.push("Password is required.");
      return errors;
    },
  },
  methods: {
    ...mapActions("global", ["initBusiness", "initUser"]),

    focusEmail() {
      if (this.$refs.email) this.$refs.email.focus();
    },

    focusPassword() {
      if (this.$refs.password) this.$refs.password.focus();
    },

    checkResponse(response) {
      if (response.status === 400) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({ icon: "error", title: "Invalid User" });
      } else {
        localStorage.setItem(
          `${process.env.VUE_APP_STORAGE_PREFIX}code`,
          this.code
        );
        this.$router.push({ name: "home" });
      }
    },

    submit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
        return;
      }

      this.loading = true;
      this.$Progress.start();

      authService
        .authenticate({
          email: this.email,
          password: this.password,
          code: this.code,
        })
        .then((response) => {
          this.$Progress.finish();
          this.checkResponse(response);
          this.loading = false;
        })
        .catch((error) => {
          this.$Progress.finish();
          this.loading = false;
          this.$swal("Invalid Login", error?.data?.message || "Try again", "error");
        });
    },
  },
};
</script>
